import type { ApolloCache } from '@apollo/client';

import type {
  AdsPageAdFragment,
  AdTraffickingColumnFragment,
  CreateAdsPageAdMutation,
  CreateBatchAdsMutation,
} from '../../../../../../../apis/graphql';
import type { AdFormValues } from '../../../../../../../common/AdForm/adFormik';
import { appendCreatedAdToSavedAdsCache } from '../../../../../../../pages/Ads/hooks/useSaveAd/util/writeAdToCache';
import type { DuplicateBatchAdSaveContext } from '../types';

type TraffickingAdsPage = {
  __typename?: string;
  edges: unknown[];
  total: number;
};

function prependCreatedAdsToTraffickingConnections(
  cache: ApolloCache<object>,
  ads: Array<AdsPageAdFragment & AdTraffickingColumnFragment>
): void {
  if (!ads.length) return;

  cache.modify({
    fields: {
      adsV5(existing: TraffickingAdsPage | undefined) {
        if (!existing?.edges) return existing;

        const newEdges = ads.map((node) => ({
          __typename: 'AdEdgeV5' as const,
          cursor: '',
          node,
        }));

        return {
          ...existing,
          edges: [...newEdges, ...existing.edges],
          total: (existing.total ?? 0) + newEdges.length,
        };
      },
    },
  });
}

/**
 * Writes batch-created ads into Apollo cache with local embellishments (same inputs as single-ad
 * `writeAdToCache`) and prepends them onto Trafficking `adsV5` list connections so tables update
 * without a full page reload.
 */
export function writeDuplicateBatchAdsToCache(
  cache: ApolloCache<object>,
  data: CreateBatchAdsMutation,
  contexts: DuplicateBatchAdSaveContext[]
): void {
  const created = data.createAdsBatchV5;
  if (!created?.length || created.length !== contexts.length) return;

  const mergedNodes: Array<AdsPageAdFragment & AdTraffickingColumnFragment> = [];

  created.forEach((node, i) => {
    const ctx = contexts[i];
    const merged = appendCreatedAdToSavedAdsCache(
      cache,
      ctx.lineItemId,
      ctx.formValues as AdFormValues,
      node as NonNullable<CreateAdsPageAdMutation['createAd']>,
      ctx.lineItemCountries,
      undefined,
      ctx.lineItemDisplayPublisherTarget
    );
    mergedNodes.push(merged as AdsPageAdFragment & AdTraffickingColumnFragment);
  });

  prependCreatedAdsToTraffickingConnections(cache, mergedNodes);
}
